import React, {
  CSSProperties,
  forwardRef,
  KeyboardEventHandler,
  ReactNode,
  Ref,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'motion/react';
import * as motion from 'motion/react-m';
import FocusLock from 'react-focus-lock';

import { ZIndex } from '@osmosis/tailwind-ui/helpers/zIndex';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import useClickOutside from '@osmosis/tailwind-ui/hooks/useClickOutside';
import { slideVertical } from '../styles/animation';

import { KeyboardEvent } from 'react';
import { useDeviceType } from '../context/DeviceTypeProvider';

/**
 * Handle the escape key event.
 * @param {KeyboardEvent} event
 * @param {Function} action
 */
export const handleEscapeKey = (
  event: KeyboardEvent,
  action?: () => void,
  preventDefault = true,
) => {
  if (event.key === 'Escape') {
    if (preventDefault) {
      event.preventDefault?.();
      event.stopPropagation?.();
    }
    action?.();
  }
};

type Position = {
  x: number;
  y: number;
  toBottom: number;
};

export type AnimatedPopupProps = {
  isVisible: boolean;
  returnFocus?: boolean;
  hide: () => void;
  children: ReactNode;
  render: (position?: Position) => ReactNode;
  motionContainerStyle?: CSSProperties;
  hideOnScroll?: boolean;
  hideEvent?: 'scroll' | 'wheel';
  isPortal?: boolean;
  align?: string;
  disableHide?: boolean;
  verticalPosition?: 'bottom' | 'top';
  forceFixedPosition?: boolean;
};

type ContainerProps = {
  hideOnScroll: boolean;
  isPortal: boolean;
  position?: Position;
  isRendered: boolean;
  translateValue: number;
  motionContainerStyle: CSSProperties;
  children: ReactNode;
  forceFixedPosition?: boolean;
  disableAnimation?: boolean;
  onCompleteAnimation: () => void;
  hide: () => void;
};

const Container = forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
  const {
    hideOnScroll,
    isPortal,
    position,
    isRendered,
    translateValue,
    disableAnimation,

    motionContainerStyle,
    children,
    forceFixedPosition,
    onCompleteAnimation,
    hide,
  } = props;

  const handleKeyDown: KeyboardEventHandler<HTMLDivElement> = (e) => {
    handleEscapeKey(e, () => hide?.());
  };

  const motionRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Focus on any button inside.
    if (isRendered) {
      const firstFocusable = motionRef.current?.querySelector(
        'button, [tabindex]',
      ) as HTMLElement;
      firstFocusable?.focus?.();
    }
  }, [isRendered]);

  const animationProps = disableAnimation
    ? {}
    : slideVertical({
        initial: { translateY: translateValue },
        exit: { translateY: translateValue },
      });

  return (
    <Box
      ref={ref}
      style={{
        ...(isPortal && {
          position: hideOnScroll || forceFixedPosition ? 'fixed' : 'absolute',
          left: position?.x || 0,
          top: position?.y || 0,
          zIndex:
            hideOnScroll || forceFixedPosition
              ? ZIndex.POPUP
              : ZIndex.BACKGROUND_OVERLAY,
        }),
        opacity: position ? 1 : 0,
      }}
    >
      <AnimatePresence>
        {isRendered && (
          <motion.div
            onAnimationComplete={onCompleteAnimation}
            onAnimationEnd={onCompleteAnimation}
            data-testid="animated-popup"
            role="dialog"
            aria-modal="true"
            ref={motionRef}
            onKeyDown={handleKeyDown}
            {...animationProps}
            style={
              {
                position: 'absolute',
                zIndex: ZIndex.HEADER,
                ...motionContainerStyle,
              } as CSSProperties
            }
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
});

Container.displayName = 'Container';

const getPosition = (
  wrapperEl?: HTMLDivElement | null,
  containerEl?: HTMLDivElement | null,
  align?: string,
  verticalPosition?: 'bottom' | 'top',
): Position | undefined => {
  if (typeof window === 'undefined') return undefined;

  const PADDING = 16;
  let wrapperX = 0,
    wrapperHeight = 0,
    wrapperWidth = 0,
    wrapperTop = 0,
    wrapperLeft = 0,
    childHeight = 0,
    containerHeight = 0,
    containerWidth = 0;

  if (wrapperEl) {
    ({
      left: wrapperLeft,
      top: wrapperTop,
      x: wrapperX,
      width: wrapperWidth,
      height: wrapperHeight,
    } = wrapperEl.getBoundingClientRect());
    ({ offsetHeight: childHeight } = wrapperEl);
  }

  if (containerEl) {
    ({ height: containerHeight, width: containerWidth } =
      containerEl.getBoundingClientRect());
  }

  let y = wrapperTop + childHeight;
  let x;
  switch (align) {
    case 'center':
      x = wrapperLeft - (containerWidth / 2 - wrapperWidth / 2);
      break;
    case 'right':
      x = wrapperLeft - (containerWidth - wrapperWidth);
      break;
    case 'left':
    default:
      x = wrapperLeft;
  }

  // Off the right side of the screen
  if (wrapperX + containerWidth > window.innerWidth) {
    const deltaX = window.innerWidth - (wrapperX + containerWidth + PADDING);
    x = wrapperLeft + deltaX;
  }

  // Clamp max height when we're close to bottom
  let toBottom = window.innerHeight - y - PADDING;

  // Flip vertical if we're within a certain distance from the bottom
  if (toBottom < 300) {
    verticalPosition = 'top';
    // Clamp height when above the element
    const MAX_TOP_HEIGHT = 500;
    containerHeight =
      containerHeight > MAX_TOP_HEIGHT ? MAX_TOP_HEIGHT : containerHeight;

    toBottom = containerHeight;
  }

  // Off the bottom of the screen
  if (verticalPosition === 'top') {
    y = y - containerHeight - wrapperHeight - PADDING;
  }

  return { x, y: y < 0 ? 0 : y, toBottom };
};

/**
 * AnimatePopup.
 */
export const AnimatedPopup = (props: AnimatedPopupProps) => {
  const {
    isVisible,
    returnFocus = true,
    hide,
    children,
    motionContainerStyle = {},
    render,
    hideOnScroll = true,
    hideEvent = 'scroll',
    isPortal = true,
    disableHide = false,
    align = 'left',
    verticalPosition = 'bottom',
    forceFixedPosition = false,
  } = props;
  const [position, setPosition] = useState<Position | undefined>(undefined);

  const { isMobile } = useDeviceType();

  const translateValue = verticalPosition === 'bottom' ? -10 : 10;

  // Ref for the component wrapper including the child
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Ref for the popup in the portal
  const portalRef = useRef<HTMLDivElement>(null);

  // Ref for the popup container
  const containerRef = useRef<HTMLDivElement>(null);

  // Whether or not the content is actually rendered to the dom
  // this should follow isVisible, however we control it here to allow for
  // animating out when isVisible is toggled to false from the parent
  const [isRendered, setIsRendered] = useState(false);

  // Local visible state so we can animate properly
  const [isAnimatedVisible, setIsAnimatedVisible] = useState(false);

  // Flag for whether we're in SSR or not
  const [isClient, setIsClient] = useState(false);

  const handleAnimateHide = () => {
    setIsAnimatedVisible(false);
  };

  useClickOutside([wrapperRef, portalRef], (e) => {
    if (isVisible && !disableHide) {
      handleAnimateHide();
      if (isMobile) {
        hide();
      }
    }
  });

  // Set that we're on the client, as you can't use portals on SSR
  useEffect(() => {
    setIsClient(true);
  }, []);

  /**
   * Uses a series of effect hooks to properly adjust position before animating
   * 1. Render invisible content
   * 2. Set position of content
   * 3. Animate content.
   */
  useEffect(() => {
    if (isVisible) {
      // Show the component content (invisible) so that it can be adjusted
      setIsRendered(true);
    } else if (isAnimatedVisible) {
      // Otherwise if we're still visible via animation then show the hide animation
      handleAnimateHide();
    }
  }, [isVisible, isAnimatedVisible]);

  const handleScrollHide = (e: Event) => {
    // Hide if we're not scrolling in the child container
    if (!containerRef.current?.contains?.(e.target as Element)) {
      handleAnimateHide();
    }
  };

  const onCompleteAnimation = () => {
    if (!isAnimatedVisible) {
      hide();
    }
  };

  useEffect(() => {
    if (isRendered) {
      // Set the position now that the container is visible but not positioned
      setPosition(
        getPosition(
          wrapperRef.current,
          containerRef.current,
          align,
          verticalPosition,
        ),
      );

      // Trigger animation
      setIsAnimatedVisible(true);
      // Add a scroll listener to hide on scroll
      if (hideOnScroll) {
        window.addEventListener(hideEvent, handleScrollHide, true);
      } else {
        window.removeEventListener(hideEvent, handleScrollHide, true);
      }
    }

    return () => {
      window.removeEventListener(hideEvent, handleScrollHide, true);
    };
  }, [isRendered, hideOnScroll, verticalPosition]);

  useEffect(() => {
    if (!isAnimatedVisible) {
      setIsRendered(false);
    }
  }, [isAnimatedVisible]);

  const containerProps = {
    isPortal,
    position,
    isRendered,
    translateValue,
    motionContainerStyle,
    verticalPosition,
    hideOnScroll,
    forceFixedPosition,
    hide: handleAnimateHide,
    onCompleteAnimation,
    disableAnimation: isMobile,
  };

  const component = isVisible ? (
    <FocusLock disabled={!isVisible} returnFocus={returnFocus}>
      <Container
        {...containerProps}
        ref={portalRef as Ref<HTMLDivElement> | undefined}
      >
        <Box ref={containerRef}>{render(position)}</Box>
      </Container>
    </FocusLock>
  ) : undefined;

  return (
    <Box ref={wrapperRef}>
      {children}
      {isClient &&
        (isPortal ? createPortal(component, document?.body) : component)}
    </Box>
  );
};

export default AnimatedPopup;
